import React, { useState } from 'react';
import { IntlShape, FormattedMessage } from 'react-intl';

import {
  FlowProgressHeader,
  StepMatch,
} from 'views/components/FlowProgressHeader/FlowProgressHeader';
import { Pricing } from 'views/components/Pricing/PricingController';
import { FlowButtonBar } from 'views/components/FlowButtonBar/FlowButtonBar';
import { ISummary } from 'types';
import {
  PRIVACY_POLICY_URL, ADDITIONAL_COMMENTS_MAX_LENGTH, OPTION_OTHER,
} from 'store/modules/app/constants';
import { SelectInput } from 'views/components/SelectInput/SelectInput';
import { getErrorMessageFor } from 'tools/utilities/getErrorMessageFor';
import { CheckboxInput } from 'views/components/CheckboxInput/CheckboxInput';
import { TextArea } from 'views/components/TextArea/TextArea';
import { scrollToError } from 'tools/utilities/scrollToError';
import { TextInput } from 'views/components/TextInput/TextInput';
import { store } from 'store/main-reducer';
import { getReferralCode } from 'store/modules/sign-up/selectors';
import { TextBlock } from './TextBlock/TextBlock';

interface IProps {
  intl: IntlShape;
  goToNextStep: (summary: ISummary) => void;
  goToPreviousStep: () => void;
  saveSummaryValues: (summary: ISummary) => void;
  defaultValues?: ISummary;

  isAccountant: boolean;
  hasSkippedAccountantDetails?: boolean;
  accountantInfo: string[];
  accountantWillPaySmartPayrollFees: boolean;
  shouldAddToAccountantsUmbrellaPage?: boolean;
  umbrellaPageName?: string;

  contactDetails: string[];
  isAdditionalPrimaryContact?: boolean;
  additionalContactDetails?: string[];

  companyName?: string;
  irdNumber?: string;
  industryType?: string;

  accountantHasAccessTo?: string;

  businessAddress: string[];
  postalAddress: string[];

  processingPayroll: string;
  payePaymentsToIrd: string;
  employeeAnnualLeaveRollover: string;
  averageGrossAmountPerPayRun: number;
  authoriseDirectDebitMonthlyFees: string;
  payrollInformation: string[];
  notAuthorisedButRequestDirectDebitAuthority: boolean;

  isViewportSmall: boolean;

  additionalComments: string;
  howDidYouHearAboutUs: string;
  howDidYouHearAboutUsOther: string;
  sendMeInformation: boolean;

  leadSource: string[];
}

export const SummaryView = (props: IProps) => {
  const {
    intl,
    saveSummaryValues,
    goToNextStep,
    goToPreviousStep,
    isAccountant,
    accountantInfo,
    accountantWillPaySmartPayrollFees,
    shouldAddToAccountantsUmbrellaPage,
    umbrellaPageName,
    contactDetails,
    additionalContactDetails,
    isAdditionalPrimaryContact,
    companyName,
    irdNumber,
    industryType,
    accountantHasAccessTo,
    businessAddress,
    postalAddress,
    processingPayroll,
    payePaymentsToIrd,
    employeeAnnualLeaveRollover,
    averageGrossAmountPerPayRun,
    authoriseDirectDebitMonthlyFees,
    payrollInformation,
    notAuthorisedButRequestDirectDebitAuthority,
    isViewportSmall,
    additionalComments,
    howDidYouHearAboutUs,
    howDidYouHearAboutUsOther,
    sendMeInformation,
    leadSource,
    hasSkippedAccountantDetails,
  } = props;

  const [touchedNext, setTouchedNext] = useState(false);

  const howDidYouHearAboutUsIsValid = !!howDidYouHearAboutUs;
  const howDidYouHearAboutUsOtherIsValid = howDidYouHearAboutUs !== OPTION_OTHER || (howDidYouHearAboutUs === OPTION_OTHER && !!howDidYouHearAboutUsOther);
  const isValid = howDidYouHearAboutUsIsValid && howDidYouHearAboutUsOtherIsValid;

  const referralCode = getReferralCode(store.getState());

  const onSubmit = () => {
    setTouchedNext(true);

    if (!isValid) {
      scrollToError();
      return;
    }

    goToNextStep({
      additionalComments,
      howDidYouHearAboutUs,
      howDidYouHearAboutUsOther,
      sendMeInformation,
      referralCode,
    });
  };

  const AccountantBlock = () => {
    if (hasSkippedAccountantDetails) {
      return (
        <TextBlock
          title={intl.formatMessage({ id: 'ACCOUNTANT' })}
          text={[intl.formatMessage({ id: 'I_DO_NOT_HAVE_AN_ACCOUNTANT' })]}
        />
      );
    }

    return (
      <>
        <TextBlock
          title={intl.formatMessage({ id: 'ACCOUNTANT' })}
          text={accountantInfo}
        />
        {accountantWillPaySmartPayrollFees && (
          <TextBlock
            title={intl.formatMessage({ id: 'ACCOUNTANT_WILL_PAY' })}
            text={[intl.formatMessage({ id: 'YES' })]}
          />
        )}
        {shouldAddToAccountantsUmbrellaPage && (
          <TextBlock
            title={intl.formatMessage({ id: 'ADD_CUSTOMER_TO_UMBRELLA_PAGE' })}
            text={[umbrellaPageName]}
          />
        )}
      </>
    );
  };

  const showAdditionalContact = !!additionalContactDetails && additionalContactDetails.length > 0;
  const isPrimaryText = intl.formatMessage({ id: 'PRIMARY_CONTACT' });
  let contactDetailsTitle = intl.formatMessage({ id: 'CONTACT_DETAILS' });
  let additionalContactDetailsTitle = intl.formatMessage({ id: 'ADDITIONAL_CONTACT' });

  if (isAdditionalPrimaryContact) {
    additionalContactDetailsTitle += isPrimaryText;
  }

  if (!isAdditionalPrimaryContact && showAdditionalContact) {
    contactDetailsTitle += isPrimaryText;
  }

  return (
    <div className="Summary">
      <FlowProgressHeader flowMatch={StepMatch.Summary} />

      <div className="Container">
        <div className="Grid">

          {/* Left */}
          <div className="Grid-cell u-width1of1 u-md-width1of2 u-lg-width7of12 u-xl-width2of3">
            <div className="Tile">
              <div className="Summary-container">
                <div className="Summary-title">
                  <FormattedMessage tagName="h2" id="SUMMARY_TEXT" />
                </div>

                <div className="Summary-block">
                  {isAccountant && <AccountantBlock />}

                  <TextBlock
                    title={contactDetailsTitle}
                    text={contactDetails}
                  />

                  {showAdditionalContact && (
                    <TextBlock
                      title={additionalContactDetailsTitle}
                      text={additionalContactDetails || []}
                    />
                  )}
                </div>

                <div className="Summary-block">
                  <TextBlock
                    title={intl.formatMessage({ id: 'COMPANY_DETAILS' })}
                    text={[
                      companyName,
                      irdNumber,
                      industryType,
                    ]}
                  />

                  <TextBlock
                    title={intl.formatMessage({ id: 'BUSINESS_ADDRESS' })}
                    text={businessAddress}
                  />

                  <TextBlock
                    title={intl.formatMessage({ id: 'POSTAL_ADDRESS' })}
                    text={postalAddress}
                  />

                  {!isAccountant && <AccountantBlock />}

                  {!hasSkippedAccountantDetails && (
                    <TextBlock
                      title={intl.formatMessage({ id: isAccountant ? 'GIVE_ACCOUNTANT_PERMISSION_FIRST_PERSON' : 'GIVE_ACCOUNTANT_PERMISSION' })}
                      text={[
                        accountantHasAccessTo,
                      ]}
                    />
                  )}

                </div>

                <div className="Summary-block">
                  <TextBlock
                    title={intl.formatMessage({ id: 'PROCESSING_PAYROLL' })}
                    text={[
                      processingPayroll,
                    ]}
                  />

                  <TextBlock
                    title={intl.formatMessage({ id: 'FREQUENCY_OF_PAYE_PAYMENTS_TO_IRD' })}
                    text={[payePaymentsToIrd]}
                  />

                  <TextBlock
                    title={intl.formatMessage({ id: 'EMPLOYEE_LEAVE_ROLLOVER' })}
                    text={[employeeAnnualLeaveRollover]}
                  />

                  {/* ASB Bank only */}
                  {!!averageGrossAmountPerPayRun && (
                    <TextBlock
                      title={intl.formatMessage({ id: 'AVERAGE_GROSS_AMOUNT_PER_PAY_RUN' })}
                      text={[`$${averageGrossAmountPerPayRun}`]}
                    />
                  )}

                  <TextBlock
                    title={intl.formatMessage({ id: 'PAYROLL_INFORMATION' })}
                    text={payrollInformation}
                  />

                  {notAuthorisedButRequestDirectDebitAuthority && (
                    <TextBlock
                      title={intl.formatMessage({ id: 'REQUESTING_DD_FORM' })}
                      text={[intl.formatMessage({ id: 'YES' })]}
                    />
                  )}

                  <TextBlock
                    title={intl.formatMessage({ id: 'AUTHORISE_DIRECT_DEBITS_FOR_MONTHLY_FEES' })}
                    text={[authoriseDirectDebitMonthlyFees]}
                  />

                </div>

                {isViewportSmall && (
                  <Pricing isSmall />
                )}

                <div className="Summary-block">
                  {/* Additional comments NOT REQUIRED */}
                  <TextArea
                    id="additionalComments"
                    label={intl.formatMessage({ id: 'ADDITIONAL_COMMENTS' })}
                    placeholder={intl.formatMessage({ id: 'ADDITIONAL_COMMENTS_PLACEHOLDER' })}
                    onChange={(val) => {
                      saveSummaryValues({
                        additionalComments: val,
                        howDidYouHearAboutUs,
                        sendMeInformation,
                        howDidYouHearAboutUsOther,
                        referralCode,
                      });
                    }}
                    value={additionalComments}
                    maxLength={ADDITIONAL_COMMENTS_MAX_LENGTH}
                  />

                  {/* How did you hear about us */}
                  <SelectInput
                    id="howDidYouHearAboutUs"
                    label={intl.formatMessage({ id: 'HOW_DID_YOU_HEAR_ABOUT_US' })}
                    defaultValue={howDidYouHearAboutUs}
                    onChange={(val) => {
                      saveSummaryValues({
                        additionalComments,
                        howDidYouHearAboutUs: val,
                        howDidYouHearAboutUsOther,
                        sendMeInformation,
                        referralCode,
                      });
                    }}
                    options={leadSource}
                    placeholder={intl.formatMessage({ id: 'SELECT' })}
                    error={touchedNext && getErrorMessageFor(howDidYouHearAboutUsIsValid, 'ERROR_HOW_DID_YOU_HEAR_ABOUT_US', intl)}
                  />

                  {howDidYouHearAboutUs === OPTION_OTHER && (
                    <TextInput
                      className="TextInput--3of4"
                      id="processingPayrollOther"
                      value={howDidYouHearAboutUsOther}
                      onChange={(val) => {
                        saveSummaryValues({
                          additionalComments,
                          howDidYouHearAboutUs,
                          howDidYouHearAboutUsOther: val,
                          sendMeInformation,
                          referralCode,
                        });
                      }}
                      type="text"
                      label={intl.formatMessage({ id: 'HOW_DID_YOU_HEAR_ABOUT_US_OTHER' })}
                      placeholder={intl.formatMessage({ id: 'ENTER_DETAILS_PLACEHOLDER' })}
                      error={touchedNext && getErrorMessageFor(howDidYouHearAboutUsOtherIsValid, 'ERROR_HOW_DID_YOU_HEAR_ABOUT_US_OTHER', intl)}
                    />
                  )}

                  {/* Send me information */}
                  <div className="Summary-checkbox">
                    <CheckboxInput
                      id="sendMeInformation"
                      value="sendMeInformation"
                      isChecked={sendMeInformation}
                      onChange={() => {
                        saveSummaryValues({
                          additionalComments,
                          howDidYouHearAboutUs,
                          sendMeInformation: !sendMeInformation,
                          howDidYouHearAboutUsOther,
                          referralCode,
                        });
                      }}
                      label={intl.formatMessage({ id: 'SEND_ME_INFORMATION' })}
                    />
                  </div>

                  {/* Privacy policy */}
                  <p className="Summary-link">
                    <FormattedMessage id="READ" />
                    &nbsp;
                    <a className="Summary-link" href={PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer">
                      <FormattedMessage id="PRIVACY_POLICY" />
                    </a>
                  </p>
                </div>
              </div>

              <FlowButtonBar
                primaryLabel={intl.formatMessage({ id: 'BUTTON_CONFIRM' })}
                secondaryLabel={intl.formatMessage({ id: 'BUTTON_BACK' })}
                onPrimary={onSubmit}
                onSecondary={goToPreviousStep}
                showCallback={false}
              />
            </div>
          </div>

          {/* Right */}
          {!isViewportSmall && (
            <div className="Grid-cell u-width1of1 u-md-width1of2 u-lg-width5of12 u-xl-width1of3">
              <Pricing />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
